/**
 * usePending — small global store for the count of pending-approval orders.
 * Drives the top-bar badge AND the inline approval card cleanup in ChatDock.
 *
 * Polls every 6s + listens to /api/v1/stream/tickets via the existing
 * useStream consumer (the SSE feed emits ORDER_PENDING_APPROVAL,
 * ORDER_APPROVED, ORDER_REJECTED tickets — any of those triggers a refresh).
 */
import { useCallback, useEffect, useState } from 'react';
import * as api from './api';
import { useStream } from './useStream';

export function usePending() {
  const [count, setCount] = useState(0);
  const [orders, setOrders] = useState<api.OrderPublic[]>([]);

  const refresh = useCallback(async () => {
    try {
      const r = await api.orders.pending();
      setCount(r.count);
      setOrders(r.orders);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 6000);
    return () => clearInterval(id);
  }, [refresh]);

  // Trigger a refresh whenever the floor emits an approval-related ticket
  useStream({
    url: '/api/v1/stream/tickets',
    onEvent: ({ event, data }) => {
      if (event !== 'ticket') return;
      const action = (data as Record<string, unknown>)?.action;
      if (
        action === 'ORDER_PENDING_APPROVAL' ||
        action === 'ORDER_APPROVED' ||
        action === 'ORDER_REJECTED' ||
        action === 'ORDER_EXPIRED'
      ) {
        refresh();
      }
    },
  });

  return { count, orders, refresh };
}
